import { supabase } from '../supabaseClient'
import { v4 as uuidv4 } from 'uuid'
import locationService from './LocationService'
import authService from './AuthService'

class AccommodationService {
  createAccommodation = async (payload: any): Promise<any> => {
    try {
      const profile = await authService.getUserProfile()
      if (!profile || !profile.uuid) {
        throw new Error('User profile not found. Please log in again.')
      }
      const accommodationUUID = uuidv4()

      // Create location first if location data provided
      let locationUUID: string | null = null
      if (payload.location && payload.location.latitude && payload.location.longitude) {
        locationUUID = await locationService.createLocation({
          latitude: payload.location.latitude,
          longitude: payload.location.longitude,
          address: payload.location.address,
          google_place_id: payload.location.google_place_id,
          created_by: profile.uuid,
        })
      }

      const accommodationData: any = {
        uuid: accommodationUUID,
        name: payload.name,
        trip_uuid: payload.trip_uuid,
        type: payload.type || 'HOUSE',
        physical_address: payload.physical_address || payload.location?.address || '',
        access_instructions: payload.access_instructions || null,
        wifi_info: payload.wifi_info || null,
        bedrooms: payload.bedrooms || null,
        bathrooms: payload.bathrooms || null,
        max_capacity: payload.max_capacity || null,
        check_in: payload.check_in || null,
        check_out: payload.check_out || null,
        price: payload.price || null,
        currency: payload.currency || null,
        listing_link: payload.listing_link || null,
        location_uuid: locationUUID,
        created_by: profile.uuid,
      }

      const { data, error } = await supabase.from('accommodations').insert(accommodationData).select()

      if (error) {
        console.error('Error creating accommodation:', error)
        throw error
      }

      return data
    } catch (error) {
      console.error('Error in createAccommodation:', error)
      throw error
    }
  }

  updateAccommodation = async (uuid: string, payload: any): Promise<any> => {
    try {
      const profile = await authService.getUserProfile()
      if (!profile || !profile.uuid) {
        throw new Error('User profile not found. Please log in again.')
      }

      // Update location if location data changed
      let locationUUID: string | null = payload.location_uuid
      if (payload.location && payload.location.latitude && payload.location.longitude) {
        locationUUID = await locationService.createLocation({
          latitude: payload.location.latitude,
          longitude: payload.location.longitude,
          address: payload.location.address,
          google_place_id: payload.location.google_place_id,
          created_by: profile.uuid,
        })
      }

      const accommodationData: any = {
        name: payload.name,
        type: payload.type,
        physical_address: payload.physical_address || payload.location?.address || '',
        access_instructions: payload.access_instructions || null,
        wifi_info: payload.wifi_info || null,
        bedrooms: payload.bedrooms || null,
        bathrooms: payload.bathrooms || null,
        max_capacity: payload.max_capacity || null,
        check_in: payload.check_in || null,
        check_out: payload.check_out || null,
        price: payload.price || null,
        currency: payload.currency || null,
        listing_link: payload.listing_link || null,
        location_uuid: locationUUID,
        updated_by: profile.uuid,
        updated_on: new Date().toISOString(),
      }

      const { data, error } = await supabase
        .from('accommodations')
        .update(accommodationData)
        .eq('uuid', uuid)
        .select()

      if (error) {
        console.error('Error updating accommodation:', error)
        throw error
      }

      return data
    } catch (error) {
      console.error('Error in updateAccommodation:', error)
      throw error
    }
  }

  deleteAccommodation = async (uuid: string): Promise<void> => {
    try {
      const profile = await authService.getUserProfile()
      if (!profile || !profile.uuid) {
        throw new Error('User profile not found. Please log in again.')
      }

      const { error } = await supabase
        .from('accommodations')
        .update({
          deleted: true,
          deleted_on: new Date().toISOString(),
          deleted_by: profile.uuid,
        })
        .eq('uuid', uuid)

      if (error) {
        console.error('Error deleting accommodation:', error)
        throw error
      }
    } catch (error) {
      console.error('Error in deleteAccommodation:', error)
      throw error
    }
  }
}

const accommodationService = new AccommodationService()

export default accommodationService
